import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Monitor, Settings, Bot, Zap, TrendingUp, ShieldCheck, Rocket } from 'lucide-react';

// ── Scroll-Position Hook ───────────────────────────────────────────────────────
function useScrollY() {
  const [y, setY] = useState(0);
  useEffect(() => {
    const handler = () => setY(window.scrollY);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);
  return y;
}

// ── InView Hook ───────────────────────────────────────────────────────────────
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

// ── Animated Counter ──────────────────────────────────────────────────────────
function Counter({ target, suffix = '', duration = 2000 }) {
  const [ref, inView] = useInView(0.3);
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = target / (duration / 16);
    const t = setInterval(() => {
      start += step;
      if (start >= target) { setVal(target); clearInterval(t); }
      else setVal(Math.floor(start));
    }, 16);
    return () => clearInterval(t);
  }, [inView, target, duration]);
  return <span ref={ref}>{val}{suffix}</span>;
}

// ── 3D Tilt Card ──────────────────────────────────────────────────────────────
function TiltCard({ children, className = '', intensity = 15 }) {
  const ref = useRef(null);
  const frame = useRef(null);
  const onMove = useCallback((e) => {
    if (frame.current) cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.transform = `perspective(800px) rotateY(${x * intensity}deg) rotateX(${-y * intensity}deg) scale3d(1.03,1.03,1.03)`;
    });
  }, [intensity]);
  const onLeave = useCallback(() => {
    if (ref.current) ref.current.style.transform = 'perspective(800px) rotateY(0deg) rotateX(0deg) scale3d(1,1,1)';
  }, []);
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave}
      className={className}
      style={{ transition: 'transform 0.1s ease-out', transformStyle: 'preserve-3d', willChange: 'transform' }}>
      {children}
    </div>
  );
}

// ── Particle Canvas ───────────────────────────────────────────────────────────
function ParticleCanvas({ darkMode }) {
  const canvas = useRef(null);
  useEffect(() => {
    const c = canvas.current;
    const ctx = c.getContext('2d');
    let w, h, particles, raf;
    const color = darkMode ? '139,92,246' : '99,102,241';

    function resize() {
      w = c.width = c.offsetWidth;
      h = c.height = c.offsetHeight;
    }

    function init() {
      resize();
      particles = Array.from({ length: 80 }, () => ({
        x: Math.random() * w, y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 2 + 0.5, a: Math.random()
      }));
    }

    function draw() {
      ctx.clearRect(0, 0, w, h);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = w; if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${color},${p.a * 0.6})`;
        ctx.fill();
      });
      // draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(${color},${(1 - dist / 120) * 0.15})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    }

    init();
    draw();
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, [darkMode]);

  return <canvas ref={canvas} className="absolute inset-0 w-full h-full pointer-events-none" />;
}

// ── Floating 3D Orb ───────────────────────────────────────────────────────────
function FloatingOrb({ size, color, x, y, delay = 0, duration = 6 }) {
  return (
    <div className="absolute pointer-events-none" style={{ left: `${x}%`, top: `${y}%` }}>
      <div style={{
        width: size, height: size,
        background: `radial-gradient(circle at 35% 35%, ${color}cc, ${color}22)`,
        borderRadius: '50%',
        filter: 'blur(1px)',
        animation: `float ${duration}s ease-in-out ${delay}s infinite`,
        boxShadow: `0 0 60px ${color}44, inset 0 0 40px ${color}22`
      }} />
    </div>
  );
}

// ── Rotating 3D Cube ──────────────────────────────────────────────────────────

// ── Floating Tech Card ────────────────────────────────────────────────────────
function FloatingTechCard({ src, size = 90, style = {}, mouseX, mouseY, parallaxX = 0.5, parallaxY = 0.5, glowColor = '#6366f1', floatDuration = 6, floatDelay = 0, opacity = 1 }) {
  return (
    <div className="absolute" style={{
      ...style,
      transform: `translate(${mouseX * parallaxX}px, ${mouseY * parallaxY}px)`,
      transition: 'transform 0.3s ease-out',
      opacity,
      zIndex: 1,
      display: typeof window !== 'undefined' && window.innerWidth < 640 ? 'none' : 'block',
    }}>
      <div style={{ animation: `float ${floatDuration}s ease-in-out ${floatDelay}s infinite` }}>
        <div style={{
          width: size, height: size,
          borderRadius: 20,
          background: `${glowColor}18`,
          border: `1px solid ${glowColor}45`,
          backdropFilter: 'blur(12px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 0 40px ${glowColor}30, 0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)`,
          padding: 18,
        }}>
          <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', filter: `drop-shadow(0 0 10px ${glowColor}90)` }} />
        </div>
      </div>
    </div>
  );
}

// ── Section Reveal Wrapper ────────────────────────────────────────────────────
function Reveal({ children, direction = 'up', delay = 0, className = '' }) {
  const [ref, inView] = useInView(0.1);
  const transforms = { up: 'translateY(60px)', down: 'translateY(-60px)', left: 'translateX(-60px)', right: 'translateX(60px)' };
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'none' : transforms[direction],
      transition: `opacity 0.8s ease ${delay}s, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}s`
    }}>
      {children}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function DerrDigitalHomepage({ darkMode = false }) {
  const scrollY = useScrollY();
  const heroRef = useRef(null);
  const [heroHeight, setHeroHeight] = useState(0);
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const [activeFeature, setActiveFeature] = useState(0);
  const showcaseRef = useRef(null);
  const [isWide, setIsWide] = useState(() => window.innerWidth >= 1024);
  useEffect(() => {
    const upd = () => setIsWide(window.innerWidth >= 1024);
    window.addEventListener('resize', upd);
    return () => window.removeEventListener('resize', upd);
  }, []);

  useEffect(() => {
    if (heroRef.current) setHeroHeight(heroRef.current.offsetHeight);
  }, []);

  useEffect(() => {
    const color = darkMode ? '#0a0a0f' : '#f8faff';
    document.documentElement.style.background = color;
    document.body.style.background = color;
    return () => {
      document.documentElement.style.background = '';
      document.body.style.background = '';
    };
  }, [darkMode]);

  useEffect(() => {
    const handler = () => {
      const el = showcaseRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // progress 0 when section enters viewport bottom, 1 when it exits viewport top
      const progress = Math.max(0, Math.min(1, -rect.top / (rect.height - window.innerHeight)));
      setActiveFeature(Math.min(2, Math.floor(progress * 3)));
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      setMouseX((e.clientX / window.innerWidth - 0.5) * 20);
      setMouseY((e.clientY / window.innerHeight - 0.5) * 20);
    };
    window.addEventListener('mousemove', handler);
    return () => window.removeEventListener('mousemove', handler);
  }, []);

  const bg = darkMode ? '#0a0a0f' : '#f8faff';
  const textPrimary = darkMode ? '#ffffff' : '#0f0f1a';
  const textSecondary = darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(15,15,26,0.6)';
  const cardBg = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)';
  const cardBorder = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

  const heroOpacity = Math.max(0, 1 - scrollY / (heroHeight * 0.6));

  const features = [
    {
      Icon: Monitor,
      title: 'Webdesign',
      desc: 'Wir bauen Websites, die nicht nur schön aussehen – sie konvertieren. Pixel-perfekt, blitzschnell, auf jedem Gerät.',
      color: '#6366f1',
      tech: [
        { src: 'https://cdn.simpleicons.org/react/61dafb', color: '#61dafb' },
        { src: 'https://cdn.simpleicons.org/typescript/3178c6', color: '#5ca0d3' },
        { src: 'https://cdn.simpleicons.org/tailwindcss/06b6d4', color: '#06b6d4' },
      ]
    },
    {
      Icon: Settings,
      title: 'Automatisierung',
      desc: 'Repetitive Prozesse gehören der Vergangenheit an. Wir automatisieren Workflows, die dir Stunden pro Woche sparen.',
      color: '#8b5cf6',
      tech: [
        { src: 'https://cdn.simpleicons.org/python/4b8bbe', color: '#4b8bbe' },
        { src: 'https://cdn.simpleicons.org/nodedotjs/6cc24a', color: '#6cc24a' },
        { src: 'https://cdn.simpleicons.org/zapier/ff4a00', color: '#ff6b35' },
      ]
    },
    {
      Icon: Bot,
      title: 'KI-Integration',
      desc: 'Künstliche Intelligenz nahtlos in dein Business integriert. Smarte Chatbots, Analyse-Tools und maßgeschneiderte KI-Lösungen.',
      color: '#a855f7',
      tech: [
        { src: 'https://cdn.simpleicons.org/huggingface/ff9d00', color: '#ff9d00' },
        { src: 'https://cdn.simpleicons.org/python/4b8bbe', color: '#4b8bbe' },
        { src: 'https://cdn.simpleicons.org/tensorflow/ff6f00', color: '#ff8c42' },
      ]
    }
  ];

  const stats = [
    { value: 50, suffix: '+', label: 'Projekte' },
    { value: 98, suffix: '%', label: 'Zufriedenheit' },
    { value: 3, suffix: 'x', label: 'Schneller' },
    { value: 24, suffix: '/7', label: 'Support' }
  ];

  const process = [
    { step: '01', title: 'Analyse', desc: 'Wir analysieren dein Business, deine Ziele und deine Zielgruppe – tief und gründlich.' },
    { step: '02', title: 'Konzept', desc: 'Auf Basis der Analyse entwickeln wir ein maßgeschneidertes digitales Konzept.' },
    { step: '03', title: 'Umsetzung', desc: 'Agile Entwicklung mit wöchentlichen Updates und direktem Feedback-Loop.' },
    { step: '04', title: 'Launch & Support', desc: 'Perfekter Launch und dauerhafter Support – wir sind langfristig an deiner Seite.' }
  ];

  return (
    <div style={{ background: bg, color: textPrimary, minHeight: '100vh', overflow: 'clip' }}>
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-20px) rotate(3deg); }
          66% { transform: translateY(-10px) rotate(-2deg); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes shimmer-sweep {
          0%   { left: -45%; }
          100% { left: 120%; }
        }
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes glitch {
          0%, 100% { clip-path: inset(0 0 95% 0); transform: translateX(-3px); }
          20% { clip-path: inset(40% 0 50% 0); transform: translateX(3px); }
          40% { clip-path: inset(70% 0 20% 0); transform: translateX(-2px); }
          60% { clip-path: inset(20% 0 70% 0); transform: translateX(2px); }
          80% { clip-path: inset(60% 0 30% 0); transform: translateX(-1px); }
        }
        .gradient-text {
          background: linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7, #ec4899);
          background-size: 300% 300%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradient-shift 4s ease infinite;
        }
        .glow {
          box-shadow: 0 0 40px rgba(99,102,241,0.3), 0 0 80px rgba(139,92,246,0.15);
        }
        .glass {
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
        }
        .noise::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
          pointer-events: none;
          opacity: 0.4;
        }
      `}</style>

      {/* ── HERO ── */}
      <section ref={heroRef} className="relative" style={{ minHeight: '100vh', display: 'flex', alignItems: 'flex-start', paddingTop: 'clamp(90px, 13vh, 130px)', paddingBottom: 60, overflow: 'hidden' }}>
        {/* Particle background */}
        <div className="absolute inset-0">
          <ParticleCanvas darkMode={darkMode} />
        </div>

        {/* Gradient mesh background */}
        <div className="absolute inset-0" style={{
          background: darkMode
            ? 'radial-gradient(ellipse 80% 60% at 50% -10%, rgba(99,102,241,0.3) 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 80% 80%, rgba(139,92,246,0.2) 0%, transparent 50%)'
            : 'radial-gradient(ellipse 80% 60% at 50% -10%, rgba(99,102,241,0.15) 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 80% 80%, rgba(139,92,246,0.1) 0%, transparent 50%)'
        }} />

        {/* Floating Orbs */}
        <FloatingOrb size="400px" color="#6366f1" x={-5} y={-10} delay={0} duration={8} />
        <FloatingOrb size="300px" color="#8b5cf6" x={75} y={60} delay={2} duration={7} />
        <FloatingOrb size="200px" color="#a855f7" x={50} y={80} delay={1} duration={9} />
        <FloatingOrb size="150px" color="#ec4899" x={90} y={10} delay={3} duration={6} />

        {/* Floating Tech Cards */}
        <FloatingTechCard
          src="https://cdn.simpleicons.org/raspberrypi/e91e8c"
          size={100}
          style={{ right: '5%', top: '15%' }}
          mouseX={mouseX} mouseY={mouseY}
          parallaxX={0.5} parallaxY={0.4}
          glowColor="#e91e8c"
          floatDuration={7} floatDelay={0}
        />
        <FloatingTechCard
          src="https://cdn.simpleicons.org/espressif/ff6347"
          size={80}
          style={{ right: '21%', top: '33%' }}
          mouseX={mouseX} mouseY={mouseY}
          parallaxX={-0.3} parallaxY={-0.25}
          glowColor="#ff6347"
          floatDuration={8} floatDelay={1.5}
          opacity={0.85}
        />
        <FloatingTechCard
          src="https://cdn.simpleicons.org/react/61dafb"
          size={90}
          style={{ right: '7%', bottom: '22%' }}
          mouseX={mouseX} mouseY={mouseY}
          parallaxX={-0.4} parallaxY={0.3}
          glowColor="#61dafb"
          floatDuration={6.5} floatDelay={0.8}
        />
        <FloatingTechCard
          src="https://cdn.simpleicons.org/typescript/5ca0d3"
          size={65}
          style={{ right: '27%', bottom: '13%' }}
          mouseX={mouseX} mouseY={mouseY}
          parallaxX={0.25} parallaxY={-0.35}
          glowColor="#3178c6"
          floatDuration={9} floatDelay={2}
          opacity={0.75}
        />

        {/* Grid lines */}
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(${darkMode ? 'rgba(99,102,241,0.05)' : 'rgba(99,102,241,0.04)'} 1px, transparent 1px), linear-gradient(90deg, ${darkMode ? 'rgba(99,102,241,0.05)' : 'rgba(99,102,241,0.04)'} 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
          maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black, transparent)'
        }} />

        {/* Hero content */}
        <div className="relative w-full max-w-7xl mx-auto px-6 lg:px-12" style={{
          opacity: heroOpacity,
          transition: 'none'
        }}>
          <div style={{
            transform: `translateX(${mouseX * 0.2}px) translateY(${mouseY * 0.2}px)`,
            transition: 'transform 0.5s ease-out'
          }}>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 mb-8 glass" style={{
              padding: '8px 20px',
              borderRadius: 999,
              border: `1px solid ${darkMode ? 'rgba(99,102,241,0.4)' : 'rgba(99,102,241,0.3)'}`,
              background: darkMode ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.08)',
              fontSize: 14,
              color: '#8b5cf6'
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#6366f1', display: 'inline-block', animation: 'pulse 2s infinite', boxShadow: '0 0 0 4px rgba(99,102,241,0.2)' }} />
              Digitalagentur · Webdesign · KI
            </div>

            {/* Main headline */}
            <h1 style={{ fontSize: 'clamp(36px, 6vw, 90px)', fontWeight: 900, lineHeight: 1.0, letterSpacing: '-0.03em', marginBottom: 20 }}>
              <span className="gradient-text" style={{ display: 'block' }}>Dein Business.</span>
              <span style={{ display: 'block', color: textPrimary }}>Digital.</span>
              <span className="gradient-text" style={{ display: 'block' }}>Besser.</span>
            </h1>

            <p style={{ fontSize: 'clamp(14px, 1.5vw, 18px)', color: textSecondary, maxWidth: 520, lineHeight: 1.65, marginBottom: 32 }}>
              Wir bauen digitale Erlebnisse, die begeistern. Websites, Automatisierungen und KI-Lösungen für Unternehmen, die wachsen wollen.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3">
              <Link to="/kontakt" className="glow" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '13px 30px', borderRadius: 12,
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                color: '#fff', fontWeight: 700, fontSize: 15, textDecoration: 'none',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                Projekt starten →
              </Link>
              <a href="#leistungen" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '13px 30px', borderRadius: 12,
                border: `1.5px solid ${darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}`,
                color: textPrimary, fontWeight: 600, fontSize: 15, textDecoration: 'none',
                background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(10px)'
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'; e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'; }}
              >
                Leistungen entdecken
              </a>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2" style={{ transform: 'translateX(-50%)', opacity: Math.max(0, 1 - scrollY / 200) }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: textSecondary, fontSize: 12 }}>
            <span>Scroll</span>
            <div style={{
              width: 1, height: 60,
              background: `linear-gradient(to bottom, ${darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)'}, transparent)`,
              animation: 'float 2s ease-in-out infinite'
            }} />
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section style={{ padding: '56px 0', position: 'relative' }}>
        <div className="absolute inset-0" style={{
          background: darkMode
            ? 'linear-gradient(180deg, transparent, rgba(99,102,241,0.05) 50%, transparent)'
            : 'linear-gradient(180deg, transparent, rgba(99,102,241,0.03) 50%, transparent)'
        }} />
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="text-center">
                  <div style={{
                    fontSize: 'clamp(36px, 4vw, 60px)',
                    fontWeight: 900,
                    lineHeight: 1,
                    background: 'linear-gradient(135deg, #6366f1, #a855f7)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                    marginBottom: 8
                  }}>
                    <Counter target={s.value} suffix={s.suffix} />
                  </div>
                  <div style={{ color: textSecondary, fontSize: 16, fontWeight: 500 }}>{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section id="leistungen" style={{ padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
        <div className="absolute inset-0" style={{
          background: darkMode ? 'rgba(99,102,241,0.03)' : 'rgba(99,102,241,0.02)',
          borderTop: `1px solid ${cardBorder}`, borderBottom: `1px solid ${cardBorder}`
        }} />

        <div className="max-w-7xl mx-auto px-6 relative">
          <Reveal className="text-center mb-12">
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: '#8b5cf6', textTransform: 'uppercase', marginBottom: 12 }}>Was wir tun</div>
            <h2 style={{ fontSize: 'clamp(26px, 3.5vw, 52px)', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
              Digitale Lösungen,<br />
              <span className="gradient-text">die wirken.</span>
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" style={{ position: 'relative', zIndex: 1 }}>
            {features.map((f, i) => (
              <Reveal key={i} direction={i === 0 ? 'left' : i === 2 ? 'right' : 'up'} delay={i * 0.15}>
                <TiltCard className="h-full" intensity={12}>
                  <div className="h-full glass" style={{
                    padding: '32px 28px',
                    borderRadius: 18,
                    background: cardBg,
                    border: `1px solid ${cardBorder}`,
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'default'
                  }}>
                    <div style={{
                      position: 'absolute', top: 0, left: 0, right: 0, height: 2,
                      background: `linear-gradient(90deg, transparent, ${f.color}80, transparent)`,
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        position: 'absolute', top: 0, bottom: 0, width: '40%',
                        background: `linear-gradient(90deg, transparent, ${f.color}, #fff, ${f.color}, transparent)`,
                        animation: `shimmer-sweep ${2.4 + i * 0.4}s ease-in-out ${i * 0.6}s infinite`,
                      }} />
                    </div>
                    <div style={{
                      position: 'absolute', top: -40, right: -40,
                      width: 140, height: 140,
                      background: `radial-gradient(circle, ${f.color}22, transparent 70%)`,
                      borderRadius: '50%'
                    }} />

                    <div style={{ marginBottom: 16, color: f.color }}><f.Icon size={40} strokeWidth={1.3} /></div>
                    <h3 style={{ fontSize: 22, fontWeight: 800, marginBottom: 10, color: textPrimary }}>{f.title}</h3>
                    <p style={{ color: textSecondary, lineHeight: 1.65, fontSize: 14 }}>{f.desc}</p>

                    <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 6, color: f.color, fontWeight: 600, fontSize: 13 }}>
                      Mehr erfahren <span>→</span>
                    </div>
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES SHOWCASE ── */}
      {/* ── SERVICES SHOWCASE: desktop sticky / mobile cards ── */}
      {isWide ? (
        <section ref={showcaseRef} style={{ height: '200vh', position: 'relative' }}>
          <div style={{ position: 'sticky', top: 0, height: '100vh', display: 'flex', alignItems: 'center' }}>
            <div className="absolute inset-0" style={{
              background: darkMode
                ? 'radial-gradient(ellipse 60% 80% at 50% 50%, rgba(99,102,241,0.12) 0%, transparent 70%)'
                : 'radial-gradient(ellipse 60% 80% at 50% 50%, rgba(99,102,241,0.06) 0%, transparent 70%)'
            }} />
            <div className="max-w-7xl mx-auto px-6 w-full relative">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
                {/* Left: Feature list */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: '#8b5cf6', textTransform: 'uppercase', marginBottom: 16 }}>So arbeiten wir</div>
                  {features.map((f, i) => (
                    <div key={i} onClick={() => setActiveFeature(i)} style={{
                      padding: '16px 20px', borderRadius: 12, marginBottom: 8,
                      border: `1.5px solid ${activeFeature === i ? f.color + '60' : cardBorder}`,
                      background: activeFeature === i ? (darkMode ? `${f.color}15` : `${f.color}08`) : 'transparent',
                      transition: 'all 0.4s cubic-bezier(0.16,1,0.3,1)', cursor: 'pointer'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                          width: 40, height: 40, borderRadius: 10, flexShrink: 0, padding: 9,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: activeFeature === i ? `${f.color}20` : cardBg,
                          border: `1px solid ${activeFeature === i ? f.color + '40' : cardBorder}`,
                          color: activeFeature === i ? f.color : textSecondary,
                          transition: 'all 0.3s ease',
                        }}>
                          <f.Icon size={18} strokeWidth={1.5} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 15, color: activeFeature === i ? textPrimary : textSecondary, transition: 'color 0.3s' }}>{f.title}</div>
                          {activeFeature === i && (
                            <div style={{ color: textSecondary, fontSize: 13, marginTop: 3, lineHeight: 1.55 }}>{f.desc}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Right: Circle animation */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', padding: '40px 0' }}>
                  <div style={{
                    width: 300, height: 300, borderRadius: '50%', position: 'relative',
                    background: `radial-gradient(circle at 35% 35%, ${features[activeFeature].color}33, ${features[activeFeature].color}08)`,
                    border: `1px solid ${features[activeFeature].color}30`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.6s cubic-bezier(0.16,1,0.3,1)',
                    boxShadow: `0 0 60px ${features[activeFeature].color}20`
                  }}>
                    <div style={{ position: 'absolute', inset: -16, border: `1px solid ${features[activeFeature].color}20`, borderRadius: '50%', animation: 'spin-slow 8s linear infinite' }} />
                    <div style={{ position: 'absolute', inset: -38, border: `1px dashed ${features[activeFeature].color}15`, borderRadius: '50%', animation: 'spin-slow 12s linear infinite reverse' }} />
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ width: 72, height: 72, margin: '0 auto 12px', color: features[activeFeature].color, filter: `drop-shadow(0 0 16px ${features[activeFeature].color}80)`, transition: 'color 0.4s ease, filter 0.4s ease' }}>
                        {React.createElement(features[activeFeature].Icon, { size: 60, strokeWidth: 1.2 })}
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: textPrimary, transition: 'all 0.4s ease' }}>{features[activeFeature].title}</div>
                    </div>
                    {features[activeFeature].tech.map((t, i) => (
                      <div key={`${activeFeature}-${i}`} style={{
                        position: 'absolute', top: '50%', left: '50%',
                        transform: `rotate(${i * 120 + scrollY * 0.05}deg) translateX(160px) rotate(-${i * 120 + scrollY * 0.05}deg)`,
                        marginTop: -22, marginLeft: -22, opacity: 0.9, transition: 'opacity 0.4s ease',
                      }}>
                        <div style={{ width: 44, height: 44, borderRadius: 10, padding: 8, backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${t.color}22`, border: `1px solid ${t.color}55`, boxShadow: `0 0 16px ${t.color}35, 0 4px 12px rgba(0,0,0,0.4)` }}>
                          <img src={t.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', filter: `drop-shadow(0 0 4px ${t.color}90)` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section style={{ padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
          <div className="absolute inset-0" style={{
            background: darkMode
              ? 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(99,102,241,0.10) 0%, transparent 70%)'
              : 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(99,102,241,0.05) 0%, transparent 70%)'
          }} />
          <div className="max-w-2xl mx-auto px-6 relative">
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: '#8b5cf6', textTransform: 'uppercase', marginBottom: 24 }}>So arbeiten wir</div>
            {features.map((f, i) => (
              <div key={i} style={{
                padding: '20px', borderRadius: 14, marginBottom: 12,
                background: cardBg, border: `1px solid ${cardBorder}`,
                position: 'relative', overflow: 'hidden'
              }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${f.color}, transparent)` }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <div style={{ color: f.color, flexShrink: 0 }}><f.Icon size={24} strokeWidth={1.5} /></div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: textPrimary }}>{f.title}</div>
                </div>
                <p style={{ color: textSecondary, fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>{f.desc}</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  {f.tech.map((t, j) => (
                    <div key={j} style={{ width: 32, height: 32, borderRadius: 8, padding: 6, background: `${t.color}18`, border: `1px solid ${t.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={t.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── PROCESS ── */}
      <section style={{ padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="text-center mb-12">
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: '#8b5cf6', textTransform: 'uppercase', marginBottom: 12 }}>Unser Prozess</div>
            <h2 style={{ fontSize: 'clamp(26px, 3.5vw, 52px)', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
              Von der Idee zum<br />
              <span className="gradient-text">Launch in 4 Schritten.</span>
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {process.map((p, i) => (
              <Reveal key={i} direction="up" delay={i * 0.1}>
                <TiltCard intensity={8}>
                  <div className="glass h-full" style={{
                    padding: '28px 24px', borderRadius: 16,
                    background: cardBg, border: `1px solid ${cardBorder}`,
                    position: 'relative', overflow: 'hidden'
                  }}>
                    {/* Decorative background number */}
                    <div style={{
                      position: 'absolute', top: -4, right: 14,
                      fontSize: 72, fontWeight: 900, lineHeight: 1,
                      color: darkMode ? 'rgba(99,102,241,0.07)' : 'rgba(99,102,241,0.06)',
                      pointerEvents: 'none', userSelect: 'none', letterSpacing: '-0.03em',
                    }}>
                      {p.step}
                    </div>
                    {/* Step badge */}
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 38, height: 38, borderRadius: 11, marginBottom: 16,
                      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      color: '#fff', fontSize: 14, fontWeight: 800, letterSpacing: '0.02em',
                      boxShadow: '0 4px 12px rgba(99,102,241,0.35)',
                    }}>
                      {p.step}
                    </div>
                    <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 8, color: textPrimary }}>{p.title}</h3>
                    <p style={{ color: textSecondary, lineHeight: 1.6, fontSize: 13 }}>{p.desc}</p>

                    {/* Connector line */}
                    {i < process.length - 1 && (
                      <div className="hidden lg:block absolute" style={{
                        top: '50%', right: -24,
                        width: 48, height: 1,
                        background: 'linear-gradient(90deg, rgba(99,102,241,0.5), rgba(99,102,241,0))',
                        zIndex: 10
                      }} />
                    )}
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY US ── */}
      <section id="warum-wir" style={{ padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
        <div className="absolute inset-0" style={{
          background: darkMode
            ? 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(168,85,247,0.05) 100%)'
            : 'linear-gradient(135deg, rgba(99,102,241,0.04) 0%, rgba(168,85,247,0.02) 100%)',
          borderTop: `1px solid ${cardBorder}`, borderBottom: `1px solid ${cardBorder}`
        }} />

        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <Reveal direction="left">
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: '#8b5cf6', textTransform: 'uppercase', marginBottom: 12 }}>Warum DerrDigital?</div>
              <h2 style={{ fontSize: 'clamp(26px, 3vw, 48px)', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.02em', marginBottom: 20 }}>
                Wir denken digital.<br />
                <span className="gradient-text">Du wächst digital.</span>
              </h2>
              <p style={{ color: textSecondary, lineHeight: 1.7, fontSize: 15, marginBottom: 28 }}>
                Keine Templates. Keine Kompromisse. Jede Lösung ist maßgeschneidert für dein Business – gebaut für Performance, Skalierung und echte Ergebnisse.
              </p>
              <Link to="/kontakt" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '13px 30px', borderRadius: 12,
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                color: '#fff', fontWeight: 700, fontSize: 14, textDecoration: 'none',
                boxShadow: '0 12px 28px rgba(99,102,241,0.3)',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 16px 36px rgba(99,102,241,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(99,102,241,0.3)'; }}
              >
                Kostenlos beraten lassen →
              </Link>
            </Reveal>

            <div className="grid grid-cols-1 gap-2">
              {[
                { Icon: Zap,          color: '#6366f1', title: 'Blitzschnell',       desc: 'Projekte, die in Wochen live gehen – nicht Monate.' },
                { Icon: TrendingUp,   color: '#8b5cf6', title: 'Ergebnisorientiert', desc: 'Kein Bullshit. Nur Lösungen, die messbare Resultate liefern.' },
                { Icon: ShieldCheck,  color: '#a855f7', title: '100% Transparent',   desc: 'Wöchentliche Updates, direkte Kommunikation, keine Überraschungen.' },
                { Icon: Rocket,       color: '#ec4899', title: 'Zukunftssicher',     desc: 'Gebaut mit den neuesten Technologien für langfristiges Wachstum.' },
              ].map((item, i) => (
                <Reveal key={i} direction="right" delay={i * 0.1}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '12px 16px', borderRadius: 10,
                    border: `1px solid ${cardBorder}`,
                    background: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                    transition: 'border-color 0.2s, background 0.2s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = item.color + '50'; e.currentTarget.style.background = item.color + '08'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = cardBorder; e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'; }}
                  >
                    <div style={{
                      width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: item.color + '18', color: item.color,
                    }}>
                      <item.Icon size={16} strokeWidth={1.8} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: textPrimary, lineHeight: 1.3 }}>{item.title}</div>
                      <div style={{ color: textSecondary, fontSize: 12, lineHeight: 1.5, marginTop: 1 }}>{item.desc}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding: 'clamp(24px, 4vh, 56px) 0', position: 'relative', overflow: 'hidden' }}>
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 40%, #a855f7 70%, #ec4899 100%)'
        }} />
        <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.3)' }} />

        {/* Animated grid */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
          maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black, transparent)'
        }} />

        <FloatingOrb size="400px" color="#ffffff" x={-10} y={-20} delay={0} duration={10} />
        <FloatingOrb size="300px" color="#ffffff" x={80} y={60} delay={3} duration={8} />

        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <Reveal>
            <div style={{ fontSize: 'clamp(9px, 1.2vw, 12px)', fontWeight: 600, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', marginBottom: 'clamp(8px, 1.5vh, 16px)' }}>
              Bereit loszulegen?
            </div>
            <h2 style={{ fontSize: 'clamp(18px, 3vw, 36px)', fontWeight: 900, lineHeight: 1.1, color: '#fff', letterSpacing: '-0.02em', marginBottom: 'clamp(6px, 1vh, 12px)' }}>
              Dein digitaler Durchbruch wartet.
            </h2>
            <p style={{ fontSize: 'clamp(11px, 1.2vw, 14px)', color: 'rgba(255,255,255,0.75)', lineHeight: 1.5, maxWidth: 380, margin: '0 auto', marginBottom: 'clamp(12px, 2vh, 24px)' }}>
              Lass uns gemeinsam herausfinden, wie wir dein Business auf das nächste Level bringen.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/kontakt" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: 'clamp(10px, 1.5vh, 16px) clamp(20px, 3vw, 36px)', borderRadius: 12,
                background: '#fff', color: '#6366f1',
                fontWeight: 700, fontSize: 'clamp(12px, 1.3vw, 15px)', textDecoration: 'none',
                boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                Jetzt Gespräch buchen →
              </Link>
              <a href="tel:+491741628004" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: 'clamp(10px, 1.5vh, 16px) clamp(20px, 3vw, 36px)', borderRadius: 12,
                border: '2px solid rgba(255,255,255,0.4)', color: '#fff',
                fontWeight: 700, fontSize: 'clamp(12px, 1.3vw, 15px)', textDecoration: 'none',
                backdropFilter: 'blur(10px)', background: 'rgba(255,255,255,0.1)',
                transition: 'all 0.2s'
              }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
              >
                📞 Direkt anrufen
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
